'use client'

import { memo, useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'

import {
  getAffectedSlotsCallback,
  timesOfTheDaySectionTitles,
  TMSDeliverySlotsArraySlot,
  TMSReservationSlotData
} from 'modules/delivery'
import { theme } from 'shared/ui/utils/designTokens'

import useCartStore from '../../../store/cartStore'
import {
  useDeliveryDates,
  useDeliverySlots,
  useHoverPreview
} from '../hooks/useSlotGrid'
import { useSlotInteractions } from '../hooks/useSlotInteractions'
import { useReservationSubmission } from '../hooks/useSlotReservation'
import {
  handleClearSelection,
  handleDateChange
} from '../utils'

import DeliveryActionPanel from './DeliveryActionPanel'
import DeliveryDateSelector from './DeliveryDateSelector'
import DeliverySlotGrid from './DeliverySlotGrid'
import DeliveryTimeOfDayButtons from './DeliveryTimeOfDayButtons'
import ReservationConfirmModal from './ReservationConfirmModal'

import styles from './CartDeliverySlotPicker.module.scss'

interface CartDeliverySlotPickerProps {
  handleEndReservation: () => void
  isInDropdown?: boolean
  reservationSlotData?: TMSReservationSlotData
  isPostalCodeAndCitySupported: boolean
  hasCoordinates: boolean
}

const CartDeliverySlotPicker: React.FC<CartDeliverySlotPickerProps> = ({
  handleEndReservation,
  isInDropdown = false,
  reservationSlotData,
  isPostalCodeAndCitySupported,
  hasCoordinates
}) => {
  const { isLoading, cart } = useCartStore()
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const { selectedDate, setSelectedDate, dates } =
    useDeliveryDates(reservationSlotData)
  const {
    deliverySlots,
    emptyDeliverySlots,
    selectedDeliverySlots,
    setSelectedDeliverySlots,
    isFetchingSlots,
    dateWithSelection,
    allSelectedSlots,
    previousAllSelectedSlots,
    previousDateWithSelection,
    rawSlotsForSelection,
    clearCache
  } = useDeliverySlots(selectedDate, cart, reservationSlotData)

  const { hoverPreviewSlots, setHoverPreviewSlots } = useHoverPreview()

  // Memoized derived state
  const hasSelection = useMemo(
    () => allSelectedSlots.length > 0 && Boolean(dateWithSelection),
    [allSelectedSlots.length, dateWithSelection]
  )

  const isChanged = useMemo(() => {
    return (
      previousAllSelectedSlots.length !== allSelectedSlots.length ||
      previousDateWithSelection !== dateWithSelection
    )
  }, [
    previousAllSelectedSlots,
    allSelectedSlots,
    previousDateWithSelection,
    dateWithSelection
  ])

  const summaryText = useMemo(() => {
    if (!hasSelection) return ''

    const isCurrentDay = selectedDate === dateWithSelection
    const dateText = isCurrentDay
      ? 'Wybrano:'
      : `Wybrano na ${new Date(dateWithSelection!).toLocaleDateString('pl-PL')}:`

    const timesText = allSelectedSlots.map(slot => slot.time).join(', ')

    return `${dateText} ${timesText}`
  }, [hasSelection, selectedDate, dateWithSelection, allSelectedSlots])

  const { handleChangeDeliverySlot } = useSlotInteractions({
    deliverySlots,
    emptyDeliverySlots,
    setSelectedDeliverySlots
  })

  const { handleConfirmReservation } = useReservationSubmission({
    allSelectedSlots,
    rawSlotsForSelection,
    dateWithSelection,
    setSelectedDeliverySlots,
    clearCache,
    handleEndReservation
  })

  const getAffectedSlotsCallbackMemo = useCallback(
    (slot: TMSDeliverySlotsArraySlot, action: 'add' | 'remove') =>
      getAffectedSlotsCallback(
        slot,
        action,
        deliverySlots,
        emptyDeliverySlots,
        selectedDeliverySlots
      ),
    [deliverySlots, emptyDeliverySlots, selectedDeliverySlots]
  )

  const handleOpenConfirmModal = useCallback(() => {
    if (hasSelection) {
      setIsConfirmModalOpen(true)
    }
  }, [hasSelection])

  const handleCloseConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false)
  }, [])

  const handleDateChangeMemo = useCallback(
    (date: string) => handleDateChange(date, setSelectedDate),
    [setSelectedDate]
  )

  const handleTimeOfTheDayChange = useCallback((timeOfTheDay: string) => {
    const sectionTitle =
      timesOfTheDaySectionTitles[
        timeOfTheDay as keyof typeof timesOfTheDaySectionTitles
      ]

    if (!sectionTitle) return

    const sectionElement = document.querySelector(
      `[data-section-title="${sectionTitle}"]`
    )

    if (sectionElement) {
      const elementPosition = sectionElement.getBoundingClientRect().top
      const offsetPosition =
        elementPosition +
        window.pageYOffset -
        theme.dimensions.topNavMinimalistic

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }, [])

  const handleClearSelectionMemo = useCallback(() => {
    handleClearSelection(setSelectedDeliverySlots)
  }, [setSelectedDeliverySlots])

  const handleModalConfirm = useCallback(async () => {
    const success = await handleConfirmReservation()

    if (success) {
      setIsConfirmModalOpen(false)
    }
  }, [handleConfirmReservation])

  return (
    <div className={styles.wrapper}>
      {!isInDropdown && <div className={styles.title}>Termin dostawy</div>}

      {!isPostalCodeAndCitySupported && cart?.deliveryAddress && (
        <div className={styles.noDelivery}>
          Na chwilę obecną nie jesteśmy w stanie dowieźć zakupów pod wskazany
          adres.
        </div>
      )}
      {!cart?.deliveryAddress && !hasCoordinates && (
        <div className={styles.noDelivery}>
          Podaj swój adres, aby sprawdzić czy do Ciebie dojedziemy
        </div>
      )}
      {cart?.deliveryAddress && !hasCoordinates && (
        <div className={styles.noDelivery}>
          Potwierdź swój adres, aby sprawdzić czy do Ciebie dojedziemy
        </div>
      )}
      {isPostalCodeAndCitySupported &&
        cart?.deliveryAddress &&
        hasCoordinates && (
          <>
            <DeliveryDateSelector
              dates={dates}
              selectedDate={selectedDate}
              dateWithSelection={dateWithSelection}
              onDateChange={handleDateChangeMemo}
            />

            <DeliveryTimeOfDayButtons
              onTimeOfDayChange={handleTimeOfTheDayChange}
            />

            <DeliverySlotWrapper>
              <DeliverySlotGrid
                isFetchingSlots={isFetchingSlots}
                deliverySlots={deliverySlots}
                emptyDeliverySlots={emptyDeliverySlots}
                selectedDeliverySlots={selectedDeliverySlots}
                hoverPreviewSlots={hoverPreviewSlots}
                setHoverPreviewSlots={setHoverPreviewSlots}
                handleChangeDeliverySlot={handleChangeDeliverySlot}
                getAffectedSlotsCallback={getAffectedSlotsCallbackMemo}
              />

              <DeliveryActionPanel
                hasSelection={hasSelection}
                summaryText={summaryText}
                isChanged={isChanged}
                isLoading={isLoading}
                onClearSelection={handleClearSelectionMemo}
                onOpenConfirmModal={handleOpenConfirmModal}
              />
            </DeliverySlotWrapper>

            <ReservationConfirmModal
              isOpen={isConfirmModalOpen}
              onClose={handleCloseConfirmModal}
              onConfirm={handleModalConfirm}
              selectedSlots={allSelectedSlots}
              selectedDate={dateWithSelection}
              isLoading={isLoading}
            />
          </>
        )}
    </div>
  )
}

export default memo(CartDeliverySlotPicker)

export const DeliverySlotWrapper = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={clsx(styles.deliverySlotWrapper, className)} {...props} />
)
